import AgencyHelper from 'features/Agency/agencyHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import KTBSButton, {
    KTBSButtonColor,
    KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import Utils from 'general/utils/Utils';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import ToastHelper from './ToastHelper';

const AgencyUIHelper = {

    // MARK: ---- CARDS ----
    buildTableInfo: (title, content) => {
        const filteredContent = content?.filter((item) => !item.hide);

        const TableRow = ({ item, isLast }) => {
            const textRef = useRef(null);
            const [isOverflowing, setIsOverflowing] = useState(false);

            useEffect(() => {
                const element = textRef.current;
                if (element) {
                    setIsOverflowing(element.scrollWidth > element.clientWidth);
                }
            }, [item?.value]);

            const textStyles = {
                color: item?.color || AppColors.gray[800],
                fontWeight: 600,
                fontSize: Utils.returnFontSize(13),
                cursor: item?.action ? 'pointer' : 'default',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
            };

            const TextContent = (
                <div
                    ref={textRef}
                    className="font-weight-bolder text-right"
                    style={textStyles}
                    onClick={item?.action ?? (() => { })}
                >
                    <span>{item?.value || ''}</span>
                </div>
            );

            return (
                <>
                    {!item?.notShow && (
                        <div
                            className="d-flex justify-content-between p-3"
                            style={{
                                borderBottom: isLast ? 'none' : `1px dashed ${AppColors.gray[300]}`,
                            }}
                        >
                            <div
                                className="mr-3"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: 500,
                                    fontSize: Utils.returnFontSize(13),
                                }}
                            >
                                {item?.name}
                            </div>
                            <div
                                className="d-flex gap-2 align-items-center"
                                style={{
                                    cursor: item?.action ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                    flex: 1,
                                }}
                            >
                                <div style={{ overflow: 'hidden', flex: 1 }}>
                                    {isOverflowing ? (
                                        <KTTooltip offset={[0, 0]} text={item?.value}>
                                            {TextContent}
                                        </KTTooltip>
                                    ) : (
                                        TextContent
                                    )}
                                </div>
                                {item?.isCopy && (
                                    <i
                                        className="fa-regular fa-copy cursor-pointer"
                                        style={{
                                            width: 16,
                                            height: 18,
                                            color: AppColors.gray[800],
                                            flexShrink: 0,
                                        }}
                                        onClick={() =>
                                            Utils.copyTextToClipboard(item?.value, () =>
                                                ToastHelper.showSuccess('Đã sao chép')
                                            )
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {title}
                </div>
                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    {filteredContent?.map((item, index) =>
                        !item?.hide ? (
                            <TableRow key={index} item={item} isLast={index === filteredContent.length - 1} />
                        ) : null
                    )}
                </div>
            </div>
        );
    },

    buildAccountInfoCard: (agencyDetail, parentAgencyDetail, isAgencyIndividual, agencyRole, isAdminAgency = false) => {
        return (
            <div>
                {AgencyUIHelper.buildTableInfo(t('Thông tin cơ bản'), [
                    {
                        name: isAgencyIndividual ? t('Họ và tên') : t('Tên tổ chức/doanh nghiệp'),
                        value: agencyDetail?.name,
                        color: AppColors.gray[800],
                    },
                    {
                        name: t('Mã số thuế'),
                        value: agencyDetail?.taxCode,
                        color: AppColors.gray[800],
                        hide: isAgencyIndividual,
                    },
                    {
                        name: t('Số điện thoại'),
                        value: agencyDetail?.phone,
                        color: AppColors.gray[800],
                    },
                    { name: t('Email'), value: agencyDetail?.email, color: AppColors.gray[800] },
                    {
                        name: t(`agencyType_${agencyRole}`),
                        value: t(`accountType_${agencyDetail?.type}`),
                        color: AppColors.gray[800],
                    },
                    {
                        name: t('Cấp đại lý'),
                        value: (
                            <>
                                <span className="min-w-100px">
                                    <KTLabel
                                        text={DealerHelper.getAgencyLevel(agencyDetail?.deepLevel)}
                                        isInline={true}
                                        size={KTLabelSize.medium}
                                        additionalClassName={`label-light-primary`}
                                    />
                                </span>
                            </>
                        ),
                        color: AppColors.gray[800],
                        hide: agencyRole !== "SALE"
                    },
                    {
                        name: agencyRole === "SALE" ? t('Đại lý quản lý') : t('Đối tượng quản lý'),
                        value: parentAgencyDetail?.name,
                        color: AppColors.primary,
                        action: () => {
                            DealerHelper.navigateToParentAgency(parentAgencyDetail);
                        },
                        hide: isAdminAgency,
                    },
                    {
                        name: t('Link giới thiệu'),
                        value: AgencyHelper.Utils.getSignUpLink(agencyDetail),
                        isCopy: true,
                        hide: agencyRole === 'CHARGEPOINT',
                    },
                    {
                        name: t('Mã giới thiệu'),
                        value: agencyDetail?.referralCode,
                        isCopy: true,
                        hide: agencyRole === 'CHARGEPOINT',
                    },
                ])}
            </div>
        );
    },

    buildEKYCInfoCard: (agencyDetail) => {
        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {t('Thông tin định danh eKYC')}
                </div>

                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    <div
                        className="d-flex justify-content-between p-3"
                        style={{
                            borderBottom: !agencyDetail?.ekycVerified ? '' : `1px dashed ${AppColors.gray[300]}`,
                        }}
                    >
                        <div
                            className="font-weight-bold"
                            style={{
                                color: AppColors.gray[600],
                                fontWeight: '500',
                                fontSize: Utils.returnFontSize(13),
                            }}
                        >
                            {t('status')}
                        </div>
                        <div
                            className="font-weight-bolder"
                            style={{
                                color: AppColors.gray[800],
                                fontWeight: '600',
                                fontSize: Utils.returnFontSize(14),
                            }}
                            onClick={() => { }}
                        >
                            <KTLabel
                                text={agencyDetail?.ekycVerified ? t(`Đã xác thực`) : t(`Chưa xác thực`)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-${agencyDetail?.ekycVerified ? 'success' : 'danger'
                                    }`}
                            />
                        </div>
                    </div>
                    {agencyDetail?.ekycVerified ? (
                        <div className="d-flex justify-content-between pt-3 mx-0 mb-3 px-3">
                            <div
                                className="w-120px font-weight-bold"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: '400',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                            >
                                {t('Dữ liệu xác thực')}
                            </div>
                            <div
                                className="font-weight-bolder cursor-pointer"
                                style={{
                                    color: AppColors.primary,
                                    fontWeight: '600',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                                onClick={() => {
                                    if (agencyDetail?.idCardDocuments?.length)
                                        DealerHelper.handleShowFile(agencyDetail?.idCardDocuments);
                                }}
                            >
                                {agencyDetail?.idCardDocuments?.length
                                    ? `Xem tệp đính kèm (${agencyDetail?.idCardDocuments?.length})`
                                    : ''}
                            </div>
                        </div>
                    ) : (
                        <div className="px-3 mb-3">
                            <KTBSButton
                                iconElement={
                                    <i className="fa-regular fa-shield-check pr-0" style={{ fontSize: 15 }} />
                                }
                                color={KTBSButtonColor.secondary}
                                title={t('Xác thực')}
                                size={KTBSButtonSize.large}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                buttonTitleFontSize={13}
                                onPress={() => {
                                    Utils.onEkyc('123456');
                                }}
                                buttonTitleColor={AppColors.gray[800]}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    },

    buildDocumentInfoCard: (agencyDetail, isAgencyIndividual) => {
        const idCardDocuments = agencyDetail?.ekycVerified === 1
            ? agencyDetail?.idCardDocuments?.slice(1) ?? []
            : agencyDetail?.idCardDocuments ?? []
        return (
            <div>
                {isAgencyIndividual
                    ? AgencyUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Loại giấy tờ định danh'),
                            value: isAgencyIndividual
                                ? agencyDetail?.idCardType === 'CC'
                                    ? 'CCCD'
                                    : 'Hộ chiếu'
                                : 'Giấy phép kinh doanh',
                        },
                        {
                            name: isAgencyIndividual
                                ? agencyDetail?.idCardType === 'CC'
                                    ? 'Số CCCD'
                                    : 'Số hộ chiếu'
                                : 'Số giấy phép kinh doanh',
                            value: agencyDetail?.idCardNumber,
                            hide: !isAgencyIndividual,
                        },
                        {
                            name: t('Ngày cấp'),
                            value: Utils.formatDateTimeToString(agencyDetail?.idCardIssueDate, 'DD/MM/YYYY'),
                        },
                        {
                            name: t('Nơi cấp'),
                            value: agencyDetail?.idCardPlaceOfIssue,
                            hide: agencyDetail?.idCardType !== 'CC',
                        },
                        {
                            name: t('Hình ảnh giấy tờ'),
                            value: idCardDocuments?.length
                                ? `Xem tệp đính kèm (${idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (idCardDocuments?.length)
                                    DealerHelper.handleShowFile(idCardDocuments);
                            },
                        },
                    ])
                    : AgencyUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Giấy phép ĐKKD'),
                            value: agencyDetail?.idCardDocuments?.length
                                ? `Xem tệp đính kèm (${agencyDetail?.idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (agencyDetail?.idCardDocuments?.length)
                                    DealerHelper.handleShowFile(agencyDetail?.idCardDocuments);
                            },
                        },
                    ])}
            </div>
        );
    },

    buildOtherInfoCard: (agencyDetail, agencyParentArea, agencyArea, agencyBank, isAgencyIndividual, isAdminRole, isAgencyInfo) => {
        return (
            <div>
                {AgencyUIHelper.buildTableInfo(t('Thông tin khác'), [
                    {
                        name: t('Người đại diện pháp luật'),
                        value: agencyDetail?.legalRepresentative,
                        color: AppColors.gray[800],
                        hide: isAgencyIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: agencyDetail?.gender ? t(`gender_${agencyDetail?.gender}`) : '',
                        hide: isAgencyIndividual,
                    },
                    {
                        name: t('Chức vụ người đại diện'),
                        value: agencyDetail?.legalPosition,
                        color: AppColors.gray[800],
                        hide: isAgencyIndividual,
                    },
                    {
                        name: t('Quốc gia'),
                        value: AgencyHelper.Data.CountryOptions?.find(country => country?.value === agencyDetail?.country)?.name,
                    },
                    {
                        name: t('Tỉnh/Thành phố'),
                        value: agencyParentArea?.name,
                    },
                    { name: t('Phường/Xã'), value: agencyArea?.name },
                    { name: t('Địa chỉ'), value: agencyDetail?.address },
                    {
                        name: t('Ngày sinh'),
                        value: agencyDetail?.dob ? Utils.formatDateTimeToString(agencyDetail?.dob, 'DD/MM/YYYY') : '',
                        hide: !isAgencyIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: agencyDetail?.gender ? t(`gender_${agencyDetail?.gender}`) : '',
                        hide: !isAgencyIndividual,
                    },
                    {
                        name: t('MST cá nhân'),
                        value: agencyDetail?.taxCode,
                        hide: !isAgencyIndividual,
                    },
                    {
                        name: t('Số tài khoản ngân hàng'),
                        value: agencyBank?.bankAccount,
                        hide: isAdminRole || isAgencyInfo,
                    },
                    {
                        name: t('Ngân hàng'),
                        value: agencyBank?.bankName,
                        hide: isAdminRole || isAgencyInfo,
                    },
                ])}
            </div>
        );
    },

    buildHsmAgreementCard: (agencyDetail) => {
        return (
            <div>
                {AgencyUIHelper.buildTableInfo(t('Thông tin chữ ký số'), [
                    {
                        name:
                            <div className='d-flex gap-1 cursor-pointer'>
                                <span>Số thỏa thuận (Agreement UUID)</span>
                                <span>
                                    <KTTooltip
                                        text={`<div><span>Dùng để kích hoạt tính năng ký số ứng với hợp đồng và các văn bản điện tử.</span></div>`}
                                        textHeader="Số thỏa thuận (Agreement UUID)"
                                        isHtmlContent
                                    >
                                        <i
                                            className="fa-regular fa-info-circle"
                                            style={{ color: AppColors.info }}
                                        />
                                    </KTTooltip>
                                </span>
                            </div>,
                        value: agencyDetail?.hsmAgreementUUID,
                    },
                ])}
            </div>
        );
    },

    // MARK: ---- DIALOGS ----
    buildShowFileDialog: (fileList, onClosed = () => { }) => {
        return (
            <div
                className="rounded-bottom-lg p-5"
                style={{
                    backgroundColor: AppColors.gray[100],
                }}
            >
                <div
                    className="bg-white rounded"
                    style={{
                        border: `1px solid ${AppColors.gray[300]}`,
                    }}
                >
                    {fileList?.map((document, index) => (
                        <div
                            key={index}
                            className="font-weight-boldest p-3 cursor-pointer"
                            style={{
                                color: AppColors.primary,
                                borderBottom:
                                    index !== fileList?.length - 1 ? `1px dashed ${AppColors.gray[300]}` : '',
                            }}
                            onClick={() => {
                                DealerHelper.handleShowImage(fileList[index], Utils.getMediaUrl(document));
                            }}
                        >
                            {fileList[index]}
                        </div>
                    ))}
                </div>
            </div>
        );
    },


    // MARK: ---- BUTTONS ----
    buildToggleSwitch: (isOn, handleToggle) => {
        return (
            <div
                className="position-relative d-inline-block"
                style={{
                    width: 43,
                    height: 26,
                }}
                onClick={() => handleToggle()}
            >
                <div
                    className="rounded-pill"
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: isOn ? AppColors.primary : AppColors.gray[400],
                        transition: 'background-color 0.3s ease',
                        cursor: 'pointer',
                    }}
                />
                <div
                    className="position-absolute rounded-circle"
                    style={{
                        width: 22,
                        height: 22,
                        backgroundColor: 'white',
                        top: 2,
                        left: isOn ? 19 : 2,
                        transition: 'left 0.3s ease',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    }}
                />
            </div>
        );
    },
};

export default AgencyUIHelper;
